import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ApplicationService } from 'src/app/services/application-service';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PersonalInfoService } from 'src/app/services/personal-info.service';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { Observable, Subscription } from 'rxjs';
import { concatMap, debounceTime } from 'rxjs/operators';
import { ApplicationsApiService } from 'src/app/services/api/applications-api.service';
import {
  AddGuarantorToApplicationRequestModel,
  AddGuarantorToApplicationResponseModel,
} from 'src/app/models/applications.model';
import { handleApiError } from 'src/app/utils/error';
import { TranslateService } from '@ngx-translate/core';
import { ContactsService } from 'src/app/services/contacts.service';
import { ContactsApiService } from 'src/app/services/api/contacts-api.service';
import {
  AddContactRequestModel,
  AddContactResponseModel,
} from 'src/app/models/contacts.model';
import { personNumberValidator } from 'src/app/validators/personnumber';
import { personNumberFormatter } from 'src/app/utils/format-personnumber';
import { FlowService } from 'src/app/services/flow.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { environment } from 'src/environments/local/environment';
import { finnishPersonNumberValidator } from 'src/app/validators/finnishPersonNumber';
import { Countries } from 'src/app/app.consts';
import { dutchPersonNumberValidator } from 'src/app/validators/dutchPersonNumber';

import { ApplicationScoreService } from 'creddo-shared-library';


@Component({
  selector: 'app-guarantor',
  templateUrl: './guarantor.component.html',
  styleUrls: ['./guarantor.component.scss'],
})
export class GuarantorComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  error!: string;
  @Input() isSummary = false;
  subs = Array<Subscription>();
  personNumberValidator!: () => ValidatorFn;
  market!: string;
  personNumberFormatter: (ssn: string) => string = (ssn) => ssn;

  constructor(
    private applicationService: ApplicationService,
    private applicationApiService: ApplicationsApiService,
    private formBuilder: FormBuilder,
    private contactsService: ContactsService,
    private contactsApiService: ContactsApiService,
    private translateService: TranslateService,
    public personalInfoService: PersonalInfoService,
    public basicInfoService: BasicInfoService,
    protected trackingService: TrackingService,
    public flowService: FlowService,
    private applicationScoreService: ApplicationScoreService,
  ) { }

  ngOnInit(): void {
    this.market = environment.country;
    const initialSelfGuarantor = this.applicationService.selfGuarantor;
    const initialAdditionalGuarantors = this.applicationService
      .additionalGuarantors;

    this.form = this.formBuilder.group({
      selfGuarantor: [initialSelfGuarantor],
      additionalGuarantors: this.formBuilder.array(initialAdditionalGuarantors),
    });
    this.subs.push(
      this.selfGuarantor.valueChanges
        .pipe(debounceTime(200))
        .subscribe((v: boolean) => {
          if (v) {
            this.applicationService.selfGuarantor = true;
            this.addSelfGuarantorToApplication();
            this.applicationScoreService.updateAppScore('guarantor1', true)
          } else {
            this.applicationService.selfGuarantor = false;
            this.removeGuarantorFromApplication(this.contactsService.contactId);
            this.applicationScoreService.updateAppScore('guarantor1', false)
          }
        })
    );

    switch (environment.country as Countries) {
      case Countries.NETHERLANDS:
        this.personNumberValidator = dutchPersonNumberValidator;
        break;
      case Countries.FINLAND:
        this.personNumberValidator = finnishPersonNumberValidator;
        if (!this.isSummary) {
            this.selfGuarantor.patchValue(true);
        }
        break;
      default:
        this.personNumberValidator = personNumberValidator;
        this.personNumberFormatter = personNumberFormatter;
        break;
    }
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  get selfGuarantor(): FormControl {
    return this.form.get('selfGuarantor') as FormControl;
  }

  get additionalGuarantors(): FormArray {
    return this.form.get('additionalGuarantors') as FormArray;
  }

  get maxGuarantorsReached(): boolean {
    return !!(
      (this.selfGuarantor.value &&
        this.additionalGuarantors.controls.length === 1) ||
      this.additionalGuarantors.controls.length === 2
    );
  }

  getAdditionalGuarantorEmailControl(index: number): FormControl {
    return this.additionalGuarantors.controls[index].get(
      'email'
    ) as FormControl;
  }

  getAdditionalGuarantorPersonNumberControl(index: number): FormControl {
    return this.additionalGuarantors.controls[index].get(
      'personNumber'
    ) as FormControl;
  }

  getAdditionalGuarantorFilled(index: number): FormControl {
    return this.additionalGuarantors.controls[index].get(
      'filled'
    ) as FormControl;
  }

  handleBackButtonClick(): void {
    this.flowService.previousScreen();
  }

  handleContinueButtonClick(): void {
    if (!this.form.valid) {
      return;
    }
    this.flowService.nextScreen();
  }

  addSelfGuarantorToApplication(): void {
    const body: AddGuarantorToApplicationRequestModel = {
      email: this.basicInfoService.email,
      personalIdNumber: this.basicInfoService.personNumber,
    };
    this.subs.push(
      this.addGuarantorToApplication(body).subscribe(
        (_) => {
          this.error = '';
          this.trackFields('guarantorSelf');
        },
        (e) => (this.error = handleApiError(e, this.translateService))
      )
    );
  }
  addAdditionalGuarantorToApplication(index: number): void {
    const controls = this.additionalGuarantors.controls[index];
    const addContactBody: AddContactRequestModel = {
      personalIdNumber: this.personNumberFormatter(
        (controls.get('personNumber') as FormControl).value
      ),
      email: (controls.get('email') as FormControl).value,
    };
    const addGuarantorBody: AddGuarantorToApplicationRequestModel = {
      ...addContactBody,
    };
    this.subs.push(
      this.contactsApiService
        .addContact(addContactBody)
        .pipe(
          concatMap((resp: AddContactResponseModel) => {
            (controls.get('contactId') as FormControl).patchValue(resp.id);
            return this.addGuarantorToApplication(addGuarantorBody, resp.id);
          })
        )
        .subscribe(() => {
          this.applicationService.additionalGuarantors.push(
            controls as FormGroup
          );
          this.error = '';
          const control = controls.get('filled') as FormControl;
          control.patchValue(true);
          if (this.applicationService.additionalGuarantors.length > 1) {
            this.trackFields('secondAdditionalGuarantor');
          } else {
            this.trackFields('additionalGuarantor');
            this.applicationScoreService.updateAppScore('guarantor2', true);
          }
        })
    );
  }

  addGuarantorToApplication(
    body: AddGuarantorToApplicationRequestModel,
    contactId?: number
  ): Observable<AddGuarantorToApplicationResponseModel> {
    const contId = contactId || this.contactsService.contactId;
    return this.applicationApiService.addGuarantorToApplication(
      this.applicationService.applicationId,
      contId,
      body
    );
  }

  addAdditionalGuarantorToForm(): void {
    this.additionalGuarantors.push(
      this.formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
        personNumber: [null, [Validators.required, this.personNumberValidator()]],
        filled: [false],
        contactId: [''],
      })
    );
  }

  removeGuarantorFromApplication(contactId: number): void {
    this.subs.push(
      this.applicationApiService
        .removeGuarantorFromApplication(
          this.applicationService.applicationId,
          contactId
        )
        .subscribe(
          () => {
            this.error = '';
            this.trackFields('guarantorRemoved');
          },
          (error) => handleApiError(error, this.translateService)
        )
    );
  }

  removeAdditionalGuarantor(index: number): void {
    this.additionalGuarantors.removeAt(index);
    this.applicationScoreService.updateAppScore('guarantor2', false);
  }

  getAdditionalGuarantorErrors(
    field: string,
    control: FormControl
  ): string | null {
    if (control.hasError('required')) {
      switch (field) {
        case 'email':
          return this.translateService.instant('global.error.emailRequired');
        case 'personNumber':
          return this.translateService.instant(
            'global.error.personNumberRequired'
          );
      }
    } else if (control.hasError('pattern')) {
      return this.translateService.instant('global.error.personNumberInvalid');
    } else if (control.hasError('email')) {
      return this.translateService.instant('global.error.emailNotValid');
    }
    return null;
  }
}
